/**
 * Asks why, before a completed report's figures change.
 *
 * A completed report's price, dates, billing flags and a partner's fee may
 * already have been billed, so changing them needs a reason (recorded with the
 * change, shown in the history). The dialog also says whether the money may
 * already have gone out — read from the Backoffice's billing records, or a
 * clearly-labelled guess from the completion month when those are unavailable.
 * It warns; it never blocks.
 */

import { useEffect, useState } from 'react';

import { Alert, Button, Group, Loader, Modal, Stack, Text, Textarea } from '@mantine/core';

import { useReportBillingStatus } from '@/hooks/useReportBillingStatus';
import { isValidReason, MIN_REASON_LENGTH } from '@/types/commercial-change';
import { describeBilledWarning, describeFields } from '@/utils/commercialChanges';

const TONE_COLOR = { warn: 'orange', caution: 'yellow', info: 'blue' } as const;

interface ReasonDialogProps {
  opened: boolean;
  reportId: string | null;
  /** The recorded field names the edit touches (price, np_fee, due_date, …). */
  changedFields: readonly string[];
  /** Set for a partner's fee / deadline, so the warning speaks about that
   * partner's billing rather than the client's. */
  assignmentId?: string | null;
  submitting?: boolean;
  onConfirm: (reason: string) => void | Promise<void>;
  onCancel: () => void;
}

export function ReasonDialog({
  opened,
  reportId,
  changedFields,
  assignmentId = null,
  submitting = false,
  onConfirm,
  onCancel,
}: Readonly<ReasonDialogProps>) {
  const [reason, setReason] = useState('');
  const { status, isLoading } = useReportBillingStatus(reportId, opened);
  const warning = describeBilledWarning(status, { assignmentId, loading: isLoading });

  // Each time the dialog opens it starts blank — a reason is for one change.
  useEffect(() => {
    if (opened) {
      setReason('');
    }
  }, [opened]);

  const valid = isValidReason(reason);
  const typed = reason.trim().length;

  return (
    <Modal
      opened={opened}
      onClose={submitting ? () => undefined : onCancel}
      title="Change a completed report"
      data-testid="reason-dialog"
    >
      <Stack gap="sm">
        <Text size="sm">
          You are changing the {describeFields(changedFields)} of a completed report. Say why — the
          reason is recorded with the change.
        </Text>

        {isLoading && (
          <Group gap="xs" data-testid="billed-loading">
            <Loader size="xs" />
            <Text size="xs" c="dimmed">
              Checking whether this has been billed…
            </Text>
          </Group>
        )}
        {warning && (
          <Alert
            color={TONE_COLOR[warning.tone]}
            variant="light"
            p="xs"
            data-testid="billed-warning"
            data-tone={warning.tone}
          >
            <Text size="sm">{warning.message}</Text>
          </Alert>
        )}

        <Textarea
          label="Reason"
          placeholder="e.g. Client agreed a revised quote after delivery"
          minRows={3}
          autosize
          value={reason}
          onChange={(event) => setReason(event.currentTarget.value)}
          description={
            valid
              ? undefined
              : `At least ${MIN_REASON_LENGTH} characters (${typed}/${MIN_REASON_LENGTH})`
          }
          data-testid="reason-input"
          data-autofocus
        />

        <Group justify="flex-end">
          <Button variant="default" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void onConfirm(reason.trim())}
            disabled={!valid}
            loading={submitting}
            data-testid="reason-confirm"
          >
            Save change
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
